import { Box, Stack } from "@mui/material";
import { useLocalStorage } from "@src/appV2/lib/utils/useLocalstorage";
import { Button } from "@src/appV2/redesign/components/Button";
import { Text } from "@src/appV2/redesign/components/Text";
import {
  type AccountSwitcherUser,
  type AdminSearchTabValue,
  type UserDetails,
  ADMIN_SEARCH_PLACEHOLDER,
  ADMIN_SEARCH_TAB_LABELS,
  ADMIN_SEARCH_TAB_OPTIONS,
  ADMIN_SEARCH_TAB_STORAGE_KEY,
} from "../../Shared/AccountSwitcher/types";
import { AccountSwitcherOptionGroupMobile } from "./AccountSwitcherOptionGroup";

interface AccountSwitcherDropdownAdminMobileProps {
  workerDetails: UserDetails;
  workplaceDetails: UserDetails;
  onUserClicked: (user: AccountSwitcherUser) => void;
  hasSearchTerm: boolean;
}

export function AccountSwitcherDropdownAdminMobile(props: AccountSwitcherDropdownAdminMobileProps) {
  const { workerDetails, workplaceDetails, onUserClicked, hasSearchTerm } = props;
  const [selectedTab, setSelectedTab] = useLocalStorage<AdminSearchTabValue>(
    ADMIN_SEARCH_TAB_STORAGE_KEY,
    "all"
  );

  if (!hasSearchTerm) {
    return (
      <Box sx={{ padding: 4, textAlign: "center" }}>
        <Text
          variant="body2"
          sx={(theme) => ({
            color: theme.palette.navbar?.itemContent,
          })}
        >
          {ADMIN_SEARCH_PLACEHOLDER}
        </Text>
      </Box>
    );
  }

  const showAgents = selectedTab === "agents" || selectedTab === "all";
  const showWorkplaces = selectedTab === "workplaces" || selectedTab === "all";
  const isAllMode = selectedTab === "all";

  return (
    <Stack>
      {/* Tabs */}
      <Stack direction="row" gap={1} justifyContent="center" sx={{ paddingY: 3 }}>
        {ADMIN_SEARCH_TAB_OPTIONS.map((tab) => (
          <Button
            key={tab}
            size="small"
            variant={selectedTab === tab ? "primary" : "outlineInverted"}
            onClick={() => setSelectedTab(tab)}
            sx={{
              textTransform: "capitalize",
              flex: 1,
            }}
          >
            {ADMIN_SEARCH_TAB_LABELS[tab]}
          </Button>
        ))}
      </Stack>

      {/* Content */}
      <Box>
        {showAgents && (
          <Box data-testid="account-switcher-agents">
            {isAllMode && (
              <Text
                variant="body2"
                sx={(theme) => ({
                  color: theme.palette.navbar?.itemContentSelected,
                  paddingX: 2,
                  paddingY: 1,
                  fontWeight: 600,
                })}
              >
                AGENTS
              </Text>
            )}
            <AccountSwitcherOptionGroupMobile
              userDetails={workerDetails}
              onUserClicked={onUserClicked}
              loadMoreLabel="Load more agents"
            />
          </Box>
        )}

        {showWorkplaces && (
          <Box sx={{ marginTop: isAllMode ? 2 : 0 }}>
            {isAllMode && (
              <Text
                variant="body2"
                sx={(theme) => ({
                  color: theme.palette.navbar?.itemContentSelected,
                  paddingX: 2,
                  paddingY: 1,
                  fontWeight: 600,
                })}
              >
                WORKPLACES
              </Text>
            )}
            <AccountSwitcherOptionGroupMobile
              userDetails={workplaceDetails}
              onUserClicked={onUserClicked}
              loadMoreLabel="Load more workplaces"
            />
          </Box>
        )}
      </Box>
    </Stack>
  );
}
